import React, { useState, useEffect, useMemo } from 'react';
import '../Css/Driverpdf.css';
import { useParams } from 'react-router-dom';
import { MdDownloadForOffline } from "react-icons/md";
import Navbar from './Navbar';
import { Typography, CircularProgress } from '@mui/material';
import Logger from './Logger';
import { PDFDownloadLink } from '@react-pdf/renderer';
import PDFDocument from './PDFDocument';
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const Driverpdf = () => {
  const adminEmail = sessionStorage.getItem('adminEmail');
  const [data, setData] = useState([]);
  const [order, setOrder] = useState([]);
  const [failedOrders, setFailedOrders] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]); // New state for failed orders
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { name, date, time } = useParams();
  const [working, setWorking] = useState();
  const [deliver, setDeliver] = useState({ assignedCount: 0, completedCount: 0, incompleteCount: 0 });
  const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/api/driver/c/${name}/${date}/${adminEmail}`);
        if (response.ok) {
          const data = await response.json();
          setData(data);

          const secondApiResponse = await fetch(`/api/driver/report/${data.email}/${time}/${adminEmail}`);
          const thirdApiResponse = await fetch(`/api/driver/deliver/${data.email}/${time}/${adminEmail}`);
          const failedOrdersResponse = await fetch(`/api/ordera/incompleted/${adminEmail}?time=${time}&email=${data.email}`);
          const activeOrdersResponse = await fetch(`/api/ordera/activeOrders/${adminEmail}?time=${time}&email=${data.email}`);
          const workingHours = await fetch(`/driver/workingHours/${data.email}/${time}`);

          if (workingHours.ok) {
            const workings = await workingHours.json();
            setWorking(workings);
          }
          if (secondApiResponse.ok) {
            const orderData = await secondApiResponse.json();
            setOrder(orderData);
          } else {
            logger.log('Failed to fetch order data', 'ERROR');
            setError('Failed to fetch order data');
          }

          if (thirdApiResponse.ok) {
            const deliverData = await thirdApiResponse.json();
            setDeliver(deliverData);
          } else {
            logger.log(`Failed to fetch delivery data ${thirdApiResponse}`, 'ERROR');
          }

          if (failedOrdersResponse.ok) {
            const failedData = await failedOrdersResponse.json();
            setFailedOrders(failedData); // Store failed orders in state
          } else {
            logger.log('Failed to fetch failed orders', 'ERROR');
          }

          if (activeOrdersResponse.ok) {
            const activeData = await activeOrdersResponse.json();
            setActiveOrders(activeData); // Store active orders in state
          } else {
            logger.log('Failed to fetch active orders', 'ERROR');
          }

          setLoading(false);
        } else {
          logger.log('Failed to fetch data', 'ERROR');
          setError('Failed to fetch data');
        }
      } catch (error) {
        logger.log(`Error fetching data: ${error.message}`, 'ERROR');
        setError('Error fetching data');
      }
    };
    fetchData();
  }, [name, date, time, adminEmail, logger]);

  if (loading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <CircularProgress />
      <Typography variant="h6" style={{ marginTop: '20px' }}>Fetching Data...</Typography>
    </div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const uniquePersonalInfo = data;
  const uniqueOrderDetails = data;

  function generateUniqueId(dateString, driverSerialNo) {
    const date = new Date(dateString);  // Convert the string to a Date object
    if (isNaN(date)) {
      return null;
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const year = date.getFullYear().toString().slice(-2);
    return `${day}${month}${year}${driverSerialNo}`;
  }

  const calculateTotalAmount = (orders, field) => {
    if (!orders || !orders.length) {
      return 0;
    }
    return orders.reduce((total, item) => total + parseFloat(item[field] || 0), 0);
  };

  const totalOrders = deliver.assignedCount + deliver.completedCount + deliver.incompleteCount;

  const mopValues = Array.from(new Set(order.flatMap(item => [item.mop, item.mop2, item.mop3].filter(value => value && value !== 'null'))));

  const formatDate = (time) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    // Format function
    const format = (date) => date.toLocaleDateString('en-GB'); // 'dd/mm/yyyy'

    switch (time) {
      case 'Today':
        return `Date: ${format(today)}`;
      case 'Yesterday':
        return `Date: ${format(yesterday)}`;
      case 'This Month':
        return `Date: ${format(startOfMonth)} - ${format(endOfMonth)}`;
      default:
        return `Date: ${time}`; // Assuming 'time' can also be a specific date string
    }
  };

  const formattedDate = formatDate(time);

  return (
    <div>
      <Navbar />
      <div className='completeS' id='pdf-content'>
        <PDFDownloadLink
          document={<PDFDocument data={data} deliver={deliver} failedOrders={failedOrders} reportId={generateUniqueId(time, data.serialNumber)} order={order} working={working} active={activeOrders} />}
          fileName={`DriverReport_${generateUniqueId(time, data.serialNumber)}.pdf`}
        >
          {({ loading }) =>
            loading ? <CircularProgress /> :
              <button
                className='generate-pdf-button'
                style={{ background: 'rgb(121, 198, 233)', border: 'none', cursor: 'pointer', padding: '10px', borderRadius: '5px' }}
                data-tooltip-id="download-tooltip"><MdDownloadForOffline size={30} /></button>
          }
        </PDFDownloadLink>

        <p>{formattedDate}</p>
        <p>Report ID: {generateUniqueId(time, data.serialNumber)}</p>
        <p>Working Hours: {working}</p>

        <div className='tile-container'>
          <div className='tile'>
            <div className='personalS'><h4>Personal Information</h4></div>
            <hr></hr>
            <div className='personalInfo'>
              {uniquePersonalInfo && (
                <>
                  <div><b>Name: </b>{uniquePersonalInfo.name}</div>
                  <div><b>Phone Number: </b>{uniquePersonalInfo.phone}</div>
                  <div><b>Email: </b>{uniquePersonalInfo.email}</div>
                  <div><b>Vehicle: </b>{uniquePersonalInfo.vehicle}</div>
                </>
              )}
            </div>
          </div>

          <div className='tile'>
            <div className='personalS'><h4>Order Details</h4></div>
            <hr></hr>
            <div className='personS'>
              {uniqueOrderDetails && (
                <>
                  <div><b>Total Orders: </b>{totalOrders}</div>
                  <div><b>Active Orders: </b>{deliver.assignedCount}</div>
                  <div><b>Completed Orders: </b>{deliver.completedCount}</div>
                  <div><b>Incomplete Orders: </b>{failedOrders.length}</div>
                </>
              )}
            </div>
          </div>
        </div>

        {mopValues.map((paymentMode, index) => {
          const filteredOrders = order.filter(item => item.mop === paymentMode || item.mop2 === paymentMode || item.mop3 === paymentMode);
          if (filteredOrders.length === 0) return null;

          return (
            <div className='bx3' key={index}>
              <div className='personS'><h5>{index + 1}. {paymentMode}:</h5>
                <table>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((item, index) => (
                      <tr key={index}>
                        <td>{item.Order_No}</td>
                        <td>₹ {item.mop === paymentMode ? item.amount1 : item.amount2}</td>
                      </tr>
                    ))}
                    <tr>
                      <td><h5>Total: ₹ </h5></td>
                      <td>
                        {calculateTotalAmount(filteredOrders.filter(item => item.mop === paymentMode), 'amount1') +
                          calculateTotalAmount(filteredOrders.filter(item => item.mop2 === paymentMode), 'amount2') +
                          calculateTotalAmount(filteredOrders.filter(item => item.mop3 === paymentMode), 'amount3')}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {/* Displaying failed orders */}
        {failedOrders.length > 0 && (
          <div className='bx4'>
            <div className='personS'><h4>Failed Delivery Orders</h4></div>
            <hr></hr>
            <div className='personS'>
              <table>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {failedOrders.map((order, index) => (
                    <tr key={index}>
                      <td>{order.Order_No}</td>
                      <td>{order.reason_failure}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Display active orders */}
        {activeOrders.length > 0 && (
          <div className='bx4'>
            <div className='personS'><h4>Active Orders</h4></div>
            <hr />
            <div className='bx'>
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Receiver Name</th>
                    <th>Receiver Address</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {activeOrders.map((order, index) => (
                    <tr key={index}>
                      <td>{order.Order_No}</td>
                      <td>{order.Rec_Name}</td>
                      <td>{order.Rec_Add}</td>
                      <td>₹ {order.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Driverpdf;
